// Chemin: C:\gestion-copro\src\services\onboarding.service.ts
// Service pour gérer l'onboarding conversationnel SENTINELLE VIGIE
// Session 108 : Chantier 0 — CRUD Firestore onboarding
// Session 109 : Corrections — tantièmes, statut occupation, localisation malfaçons, adresse postale
//               + totalTantiemes + création automatique des interlocuteurs (syndic, promoteur)
// Session 159 : Ajout écriture infos copropriété (nom, adresse structurée, CP, commune, nb lots, RNC)
// Lecture/écriture de l'état onboarding dans le document copropriete vigie

import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import {
  OnboardingState,
  OnboardingStepId,
  OnboardingData,
  GarantiesVEFA,
  ONBOARDING_INITIAL_STATE
} from '../types/onboarding.types';

// ============================================
// LECTURE
// ============================================

/**
 * Charger l'état onboarding depuis le document copropriete
 * Retourne l'état initial si aucun onboarding n'est enregistré
 */
export const getOnboardingState = async (
  coproprieteId: string
): Promise<OnboardingState> => {
  try {
    const ref = doc(db, 'coproprietes', coproprieteId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      console.warn(`[Onboarding] Copropriété ${coproprieteId} introuvable`);
      return { ...ONBOARDING_INITIAL_STATE };
    }

    const data = snap.data();

    // Si le flag onboardingComplete n'existe pas encore, c'est un ancien compte
    if (data.onboardingComplete === undefined) {
      return { ...ONBOARDING_INITIAL_STATE };
    }

    return {
      onboardingComplete: data.onboardingComplete ?? false,
      onboardingStep: data.onboardingStep ?? 'accueil',
      onboardingData: data.onboardingData ?? { dateReponses: null, coproprieteNeuve: null },
    };
  } catch (error) {
    console.error('[Onboarding] Erreur lecture état:', error);
    return { ...ONBOARDING_INITIAL_STATE };
  }
};

/**
 * Vérifier rapidement si l'onboarding est terminé
 * (pour le bandeau dashboard — évite de charger toutes les données)
 */
export const isOnboardingComplete = async (
  coproprieteId: string
): Promise<boolean> => {
  try {
    const ref = doc(db, 'coproprietes', coproprieteId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return false;
    return snap.data().onboardingComplete === true;
  } catch (error) {
    console.error('[Onboarding] Erreur vérification:', error);
    return false;
  }
};

// ============================================
// ÉCRITURE — PROGRESSION
// ============================================

/**
 * Sauvegarder la progression (étape courante + données partielles)
 * Appelé à chaque réponse de Nicole pour permettre la reprise
 */
export const saveOnboardingProgress = async (
  coproprieteId: string,
  step: OnboardingStepId,
  data: Partial<OnboardingData>
): Promise<void> => {
  try {
    const ref = doc(db, 'coproprietes', coproprieteId);

    // Lire les données existantes pour fusionner
    const snap = await getDoc(ref);
    const existingData = snap.exists()
      ? snap.data().onboardingData || {}
      : {};

    // Fusionner : les nouvelles données écrasent les anciennes champ par champ
    const mergedData = { ...existingData, ...data };

    await updateDoc(ref, {
      onboardingStep: step,
      onboardingData: mergedData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('[Onboarding] Erreur sauvegarde progression:', error);
    throw error;
  }
};

// ============================================
// ÉCRITURE — COMPLÉTION
// ============================================

/**
 * Finaliser l'onboarding : marquer comme terminé + écrire les données
 * enrichies dans le document copropriete + créer les interlocuteurs
 */
export const completeOnboarding = async (
  coproprieteId: string,
  finalData: OnboardingData
): Promise<void> => {
  try {
    const ref = doc(db, 'coproprietes', coproprieteId);
    const now = Timestamp.now();

    // Construire l'objet de mise à jour
    const updatePayload: Record<string, any> = {
      onboardingComplete: true,
      onboardingStep: 'plan_action',
      onboardingData: {
        ...finalData,
        dateReponses: now,
      },
      updatedAt: now,
    };

    // Session 159 : Écrire les infos copropriété au niveau racine
    // Ces champs alimentent le panneau admin SaaS et le contexte SENTINELLE
    if (finalData.infosCopropriete) {
      const infos = finalData.infosCopropriete;
      // Nom de la copropriété (peut avoir été corrigé pendant l'onboarding)
      if (infos.nomCopropriete) {
        updatePayload.nom = infos.nomCopropriete;
      }
      // Adresse structurée — objet adressePostale au format standard
      updatePayload.adressePostale = {
        rue: infos.adresseCopropriete || '',
        codePostal: infos.codePostal || '',
        ville: infos.commune || '',
      };
      // Champs séparés pour recherche et affichage admin
      if (infos.codePostal) {
        updatePayload.codePostal = infos.codePostal;
      }
      if (infos.commune) {
        updatePayload.ville = infos.commune;
      }
      // Nombre total de lots de la copropriété
      if (infos.nombreLotsTotal && infos.nombreLotsTotal > 0) {
        updatePayload.nombreLots = infos.nombreLotsTotal;
      }
      // Numéro RNC (Registre National des Copropriétés)
      if (infos.numeroRNC) {
        updatePayload.numeroRNC = infos.numeroRNC;
      }
    }

    // Écrire les données enrichies au niveau racine du document copropriete
    // pour qu'elles soient exploitables par SENTINELLE et les autres modules
    if (finalData.nomSyndic) {
      updatePayload.nomSyndic = finalData.nomSyndic;
    }
    if (finalData.nomPromoteur) {
      updatePayload.nomPromoteur = finalData.nomPromoteur;
    }
    if (finalData.coproprieteNeuve !== null && finalData.coproprieteNeuve !== undefined) {
      updatePayload.coproprieteNeuve = finalData.coproprieteNeuve;
    }
    if (finalData.dateLivraison) {
      updatePayload.dateLivraison = finalData.dateLivraison;
    }
    if (finalData.garanties) {
      updatePayload.garanties = finalData.garanties;
    }
    if (finalData.lots && finalData.lots.length > 0) {
      updatePayload.lots = finalData.lots;
    }
    if (finalData.totalTantiemes) {
      updatePayload.totalTantiemes = finalData.totalTantiemes;
    }
    if (finalData.csActif) {
      updatePayload.csActif = finalData.csActif;
    }
    if (finalData.relationSyndic) {
      updatePayload.relationSyndic = finalData.relationSyndic;
    }
    if (finalData.statutOccupation) {
      updatePayload.statutOccupation = finalData.statutOccupation;
    }
    if (finalData.adressePostale) {
      // Session 126 : écrire dans 'adresse' (type Adresse du modèle Copropriete)
      // Session 159 : l'adresse postale du copropriétaire est maintenant distincte
      // de l'adresse de la copropriété. On écrit les deux :
      // - 'adresse' = adresse de la copropriété (depuis infosCopropriete, traité ci-dessus)
      // - 'adresseProprietaire' = adresse postale du copropriétaire (pour courriers SCUD)
      updatePayload.adresseProprietaire = {
        rue: finalData.adressePostale.rue || '',
        codePostal: finalData.adressePostale.codePostal || '',
        ville: finalData.adressePostale.ville || '',
      };
    }
    if (finalData.localisationMalfacons) {
      updatePayload.localisationMalfacons = finalData.localisationMalfacons;
    }

    await updateDoc(ref, updatePayload);

    // Session 109 : Créer automatiquement les fiches interlocuteurs
    await creerInterlocuteursOnboarding(coproprieteId, finalData);

  } catch (error) {
    console.error('[Onboarding] Erreur finalisation:', error);
    throw error;
  }
};

// ============================================
// CRÉATION AUTOMATIQUE DES INTERLOCUTEURS
// ============================================

/**
 * Créer les fiches interlocuteurs (syndic, promoteur) à partir des données d'onboarding.
 * Vérifie d'abord qu'un interlocuteur du même type et nom n'existe pas déjà
 * (cas de reset onboarding + re-complétion).
 * Stocke les IDs créés dans le document copropriete pour référence rapide.
 */
const creerInterlocuteursOnboarding = async (
  coproprieteId: string,
  data: OnboardingData
): Promise<void> => {
  try {
    const ref = doc(db, 'coproprietes', coproprieteId);
    const interlocuteursRef = collection(db, 'interlocuteurs');
    const idsToUpdate: Record<string, string> = {};

    // --- Syndic ---
    if (data.nomSyndic) {
      const existingId = await trouverInterlocuteurExistant(
        coproprieteId, 'syndic', data.nomSyndic
      );

      if (existingId) {
        idsToUpdate.syndicInterlocuteurId = existingId;
        console.log(`[Onboarding] Syndic existant trouvé: ${existingId}`);
      } else {
        const syndicDoc = await addDoc(interlocuteursRef, {
          coproprieteId,
          type: 'syndic',
          raisonSociale: data.nomSyndic,
          contacts: [], // À compléter plus tard par Nicole
          actif: true,
          notes: `Créé automatiquement lors de l'onboarding VIGIE. Relation : ${data.relationSyndic || 'non renseignée'}.`,
          source: 'onboarding_vigie',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        idsToUpdate.syndicInterlocuteurId = syndicDoc.id;
        console.log(`[Onboarding] Syndic créé: ${syndicDoc.id}`);
      }
    }

    // --- Promoteur (copro neuve uniquement) ---
    if (data.coproprieteNeuve && data.nomPromoteur) {
      const existingId = await trouverInterlocuteurExistant(
        coproprieteId, 'promoteur', data.nomPromoteur
      );

      if (existingId) {
        idsToUpdate.promoteurInterlocuteurId = existingId;
        console.log(`[Onboarding] Promoteur existant trouvé: ${existingId}`);
      } else {
        const promoteurDoc = await addDoc(interlocuteursRef, {
          coproprieteId,
          type: 'promoteur',
          raisonSociale: data.nomPromoteur,
          contacts: [], // À compléter plus tard par Nicole
          actif: true,
          notes: 'Créé automatiquement lors de l\'onboarding VIGIE.',
          source: 'onboarding_vigie',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        idsToUpdate.promoteurInterlocuteurId = promoteurDoc.id;
        console.log(`[Onboarding] Promoteur créé: ${promoteurDoc.id}`);
      }
    }

    // Stocker les IDs dans le document copropriete
    if (Object.keys(idsToUpdate).length > 0) {
      await updateDoc(ref, {
        ...idsToUpdate,
        updatedAt: Timestamp.now(),
      });
    }
  } catch (error) {
    // Ne pas bloquer l'onboarding si la création d'interlocuteurs échoue
    console.error('[Onboarding] Erreur création interlocuteurs (non bloquante):', error);
  }
};

/**
 * Vérifier si un interlocuteur du même type et nom existe déjà pour cette copropriété.
 * Évite les doublons en cas de reset + re-complétion de l'onboarding.
 */
const trouverInterlocuteurExistant = async (
  coproprieteId: string,
  type: string,
  raisonSociale: string
): Promise<string | null> => {
  try {
    const q = query(
      collection(db, 'interlocuteurs'),
      where('coproprieteId', '==', coproprieteId),
      where('type', '==', type),
      where('raisonSociale', '==', raisonSociale)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs[0].id;
    }
    return null;
  } catch {
    return null;
  }
};

// ============================================
// UTILITAIRES — CALCUL GARANTIES
// ============================================

/**
 * Calculer les dates d'expiration des garanties à partir de la date de livraison
 * @param dateLivraison Format "YYYY-MM" (ex: "2025-09")
 * @returns Dates au format "YYYY-MM-DD"
 */
export const calculerGaranties = (dateLivraison: string): GarantiesVEFA => {
  // Parser mois/année — on prend le 15 du mois comme date de référence
  const [annee, mois] = dateLivraison.split('-').map(Number);
  const dateRef = new Date(annee, mois - 1, 15);

  // GPA : +1 an
  const gpa = new Date(dateRef);
  gpa.setFullYear(gpa.getFullYear() + 1);

  // Biennale : +2 ans
  const biennale = new Date(dateRef);
  biennale.setFullYear(biennale.getFullYear() + 2);

  // Décennale : +10 ans
  const decennale = new Date(dateRef);
  decennale.setFullYear(decennale.getFullYear() + 10);

  const format = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  return {
    gpa: format(gpa),
    biennale: format(biennale),
    decennale: format(decennale),
  };
};

/**
 * Calculer le nombre de jours restants avant expiration d'une garantie
 * @param dateExpiration Format "YYYY-MM-DD"
 * @returns Nombre de jours (négatif si expirée)
 */
export const joursRestantsGarantie = (dateExpiration: string): number => {
  const expiration = new Date(dateExpiration);
  const maintenant = new Date();
  const diffMs = expiration.getTime() - maintenant.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Formater une date de garantie pour affichage humain
 * @param dateStr Format "YYYY-MM-DD"
 * @returns Ex: "15 septembre 2026"
 */
export const formatDateGarantie = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// ============================================
// RÉINITIALISATION (pour tests / debug)
// ============================================

/**
 * Réinitialiser l'onboarding d'une copropriété vigie
 * Usage : debug uniquement — pas exposé dans l'UI
 */
export const resetOnboarding = async (
  coproprieteId: string
): Promise<void> => {
  try {
    const ref = doc(db, 'coproprietes', coproprieteId);
    await updateDoc(ref, {
      onboardingComplete: false,
      onboardingStep: 'accueil',
      onboardingData: { dateReponses: null, coproprieteNeuve: null },
      // On ne supprime PAS les données enrichies (syndic, promoteur, etc.)
      // ni les interlocuteurs créés — pour ne pas perdre de données si reset accidentel
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('[Onboarding] Erreur reset:', error);
    throw error;
  }
};
